import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import KeyShoutForm, {
	KeyShoutFormValues
} from "../../components/KeyShoutForm/KeyShoutForm";
import useKeyShout from "../../hooks/useKeyShout";
import useEditKeyShout from "../../hooks/useEditKeyShout";
import useDeleteKeyShout from "../../hooks/useDeleteKeyShout";
import { adaptEditKeyShoutPayload } from "../../adapters/adaptKeyShoutPayload";
import { formValuesToKeyShout } from "../../adapters/formValuesToKeyShout";

function keyShoutToFormValues(shout: KeyShout): KeyShoutFormValues {
	return {
		heading: shout.heading,
		content: shout.content ?? "",
		buttonLink: shout.buttonLink ?? "",
		webview: shout.webview,
		feedId: shout.feedId ?? null,
		selectedFeed: shout.feed ?? null,
		image: null,
		audienceGroupIds: (shout.audienceGroups ?? []).map((g) => g.id)
	};
}

const EditKeyShout = () => {
	const navigate = useNavigate();
	const { keyShoutId } = useParams<{ keyShoutId: string }>();
	const id = Number(keyShoutId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<KeyShoutFormValues>({
		heading: "",
		content: "",
		buttonLink: "",
		webview: true,
		feedId: null,
		selectedFeed: null,
		image: null,
		audienceGroupIds: []
	});

	const { data: keyShout } = useKeyShout(isValidId ? id : 0);
	const { mutate: editKeyShout, isLoading: isEditing } = useEditKeyShout();
	const { mutate: deleteKeyShout, isLoading: isDeleting } = useDeleteKeyShout();

	useEffect(() => {
		if (keyShout) {
			setDefaultValues(keyShoutToFormValues(keyShout));
		}
	}, [keyShout]);

	if (!isValidId) {
		return <Navigate to="/app/homepage/key-shouts" replace />;
	}

	const handleSubmit = (data: KeyShoutFormValues, imageRemoved: boolean) => {
		const payload = adaptEditKeyShoutPayload(
			formValuesToKeyShout(data, id),
			data.image,
			imageRemoved
		);

		editKeyShout(
			{ id, payload },
			{
				onSuccess: () => navigate("/app/homepage/key-shouts")
			}
		);
	};

	const handleDelete = () => {
		deleteKeyShout(id, {
			onSuccess: () => navigate("/app/homepage/key-shouts")
		});
	};

	return (
		<Page>
			<KeyShoutForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				existingImageReference={keyShout?.image ?? null}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete key shout?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditKeyShout;
